// Component Management Service
// Handles UI component creation, registration, and lifecycle management

import { FilterPanel } from '../components/FilterPanel.js';
import { MineDetailsPanel } from '../components/MineDetailsPanel.js';
import { RiskDetailsPanel } from '../components/RiskDetailsPanel.js';
import { SearchBar } from '../components/SearchBar.js';
import { Navigation } from '../components/Navigation.js';
import { StatusBar } from '../components/StatusBar.js';
import { CommodityLegend } from '../components/CommodityLegend.js';
import { FilterStatus } from '../components/FilterStatus.js';
import { NotificationSystem } from '../components/NotificationSystem.js';
import { ContactUs } from '../components/ContactUs.js';
import { PremiumMembers } from '../components/PremiumMembers.js';
import { WelcomeSplash } from '../components/WelcomeSplash.js';
import { featuredMines } from '../components/FeaturedMines.js';
import { artisanalMinesFilter } from '../components/ArtisanalMinesFilter.js';
import { appConfig } from '../config/appConfig.js';

export class ComponentManager {
  constructor() {
    this.components = new Map();
    this.isInitialized = false;
  }

  initializeComponents() {
    console.log('🧩 Initializing UI components...');

    try {
      // Navigation (handles all panel toggles)
      this.registerComponent('navigation', new Navigation({
        enableKeyboardShortcuts: true,
        enableTooltips: true
      }));

      // Search bar
      const searchContainer = document.getElementById('searchContainer');
      if (searchContainer) {
        this.registerComponent('searchBar', new SearchBar(searchContainer, {
          placeholder: 'Search mines, commodities, countries...',
          debounceMs: 300,
          enableSuggestions: true
        }));
      }

      // Filter panel
      const filterPanel = document.getElementById('filterPanel');
      if (filterPanel) {
        this.registerComponent('filterPanel', new FilterPanel(filterPanel, {
          autoLoad: true
        }));
      }

      // Mine details panel
      const mineDetailsSidebar = document.getElementById('mineDetailsSidebar');
      if (mineDetailsSidebar) {
        this.registerComponent('mineDetailsPanel', new MineDetailsPanel(mineDetailsSidebar, {
          enableFeedback: true,
          showCoordinates: true,
          showRiskData: true
        }));
      }

      // Risk details panel
      const riskDetailsSidebar = document.getElementById('riskDetailsSidebar');
      if (riskDetailsSidebar) {
        this.registerComponent('riskDetailsPanel', new RiskDetailsPanel(riskDetailsSidebar));
      }

      // Commodity legend
      const legendContent = document.getElementById('legendContent');
      if (legendContent) {
        this.registerComponent('commodityLegend', new CommodityLegend(legendContent, {
          showCounts: true,
          enableToggle: true
        }));
      }

      // Legend reflects what's actually rendered on the canvas, not the raw
      // viewport response (which can include mines outside the visible area
      // at low zoom). mapService dispatches this event after rendering settles.
      document.addEventListener('mines-rendered', (e) => {
        const legend = this.getComponent('commodityLegend');
        const mines = e.detail?.mines;
        if (legend && Array.isArray(mines)) {
          legend.updateMineData(mines);
        }
      });

      // Featured mines chips (singleton, auto-initializes)
      this.registerComponent('featuredMines', featuredMines);

      // Artisanal mines filter (singleton, auto-initializes)
      this.registerComponent('artisanalMinesFilter', artisanalMinesFilter);

      // Filter status display
      const filterStatusContainer = document.querySelector('.filter-status-container') ||
                                  this.createFilterStatusContainer();
      this.registerComponent('filterStatus', new FilterStatus(filterStatusContainer));

      // Notification system
      this.registerComponent('notificationSystem', new NotificationSystem());

      // Welcome splash
      this.registerComponent('welcomeSplash', new WelcomeSplash(appConfig.ui.welcomeSplash));

      // Contact Us modal
      this.registerComponent('contactUs', new ContactUs());
      this.components.get('contactUs').init();

      // Export immediately for early access
      window.contactUs = this.components.get('contactUs');
      console.log('✅ ContactUs exported to window:', !!window.contactUs);

      // Premium Members modal
      this.registerComponent('premiumMembers', new PremiumMembers());
      this.components.get('premiumMembers').init();

      // Export immediately for early access
      window.premiumMembers = this.components.get('premiumMembers');
      console.log('✅ PremiumMembers exported to window:', !!window.premiumMembers);

      // Status bar
      const statusBarContainer = document.getElementById('statusBar') ||
                                document.getElementById('footer');
      if (statusBarContainer) {
        this.registerComponent('statusBar', new StatusBar(statusBarContainer, {
          showFilterCount: true,
          showMineCount: true,
          showLoadingStatus: true
        }));
      }

      this.isInitialized = true;
      console.log('✅ UI components initialized');

    } catch (error) {
      console.error('❌ Error initializing components:', error);
      throw error;
    }
  }

  registerComponent(name, component) {
    if (this.components.has(name)) {
      console.warn(`⚠️ Component '${name}' already registered, replacing...`);
      // Destroy existing component if it has a destroy method
      const existing = this.components.get(name);
      if (existing && typeof existing.destroy === 'function') {
        existing.destroy();
      }
    }

    this.components.set(name, component);
    console.log(`✅ Component '${name}' registered`);
    return component;
  }

  getComponent(name) {
    const component = this.components.get(name);
    if (!component) {
      console.warn(`⚠️ Component '${name}' not found`);
    }
    return component;
  }

  hasComponent(name) {
    return this.components.has(name);
  }

  async loadFilterOptions() {
    const filterPanel = this.getComponent('filterPanel');
    if (filterPanel && typeof filterPanel.loadFilterOptions === 'function') {
      await filterPanel.loadFilterOptions();
      console.log('✅ Filter options loaded');
    }
  }

  updateMapWithMines(mines) {
    // Legend updates are driven by the 'mines-rendered' event from mapService
    // (queryRenderedFeatures-based) so the count matches what's actually on
    // screen. This stub remains for callers that still invoke it, but no
    // longer pushes the full unfiltered viewport response into the legend.
    void mines;
  }

  showSuccess(message) {
    const notificationSystem = this.getComponent('notificationSystem');
    if (notificationSystem) {
      notificationSystem.showSuccess(message);
    }
  }

  showError(message) {
    const notificationSystem = this.getComponent('notificationSystem');
    if (notificationSystem) {
      notificationSystem.showError(message);
    } else {
      // Fallback to basic alert
      alert(message);
    }
  }

  hideAllPanels() {
    const navigation = this.getComponent('navigation');
    if (navigation && typeof navigation.hideAllPanels === 'function') {
      navigation.hideAllPanels();
    }
  }

  createFilterStatusContainer() {
    const container = document.createElement('div');
    container.className = 'filter-status-container';
    container.id = 'filterStatusContainer';

    // Insert after filter panel or in footer
    const filterPanel = document.getElementById('filterPanel');
    if (filterPanel && filterPanel.parentNode) {
      filterPanel.parentNode.insertBefore(container, filterPanel.nextSibling);
    } else {
      document.body.appendChild(container);
    }

    return container;
  }

  getAllComponents() {
    return Array.from(this.components.entries()).reduce((acc, [name, component]) => {
      acc[name] = component;
      return acc;
    }, {});
  }

  getComponentNames() {
    return Array.from(this.components.keys());
  }

  destroyComponent(name) {
    const component = this.components.get(name);
    if (component) {
      if (typeof component.destroy === 'function') {
        component.destroy();
      }
      this.components.delete(name);
      console.log(`✅ Component '${name}' destroyed`);
      return true;
    }
    return false;
  }

  destroyAllComponents() {
    console.log('🧹 Destroying all components...');

    this.components.forEach((component, name) => {
      if (component && typeof component.destroy === 'function') {
        try {
          component.destroy();
          console.log(`✅ Component '${name}' destroyed`);
        } catch (error) {
          console.error(`❌ Error destroying component '${name}':`, error);
        }
      }
    });

    this.components.clear();
    this.isInitialized = false;
    console.log('✅ All components destroyed');
  }

  isComponentsInitialized() {
    return this.isInitialized;
  }
}

// Export singleton instance
export const componentManager = new ComponentManager();